import { useEffect, useState, useCallback } from "react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, inputClass } from "../components/common";
import { toast } from "sonner";
import { Clock, CheckCircle, DownloadSimple, FunnelSimple, TrendUp, Gear, Warning, FilePdf, ClockCounterClockwise, ChartBar } from "@phosphor-icons/react";

export default function Cobranza() {
  const { user } = useAuth();
  const canConfig = ["admin", "gerente_general", "gerente_administrativo"].includes(user?.role);
  const [data, setData] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [f, setF] = useState({ contractor_id: "", date_from: "", date_to: "" });
  const [rdays, setRdays] = useState("");
  const [alertThr, setAlertThr] = useState("");
  const [ph, setPh] = useState({ contractor_id: "", month: "" });
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (f.contractor_id) params.contractor_id = f.contractor_id;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/collections", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [f]);
  const loadHistory = useCallback(() => {
    const params = {};
    if (ph.contractor_id) params.contractor_id = ph.contractor_id;
    if (ph.month) params.month = ph.month;
    api.get("/payments-history", { params }).then((r) => setHistory(r.data)).catch(() => {});
  }, [ph]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    api.get("/contractors").then((r) => setContractors(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setRdays(String(r.data.factura_reminder_days ?? 3)); setAlertThr(String(r.data.cobranza_alert_threshold ?? 0)); }).catch(() => {});
  }, []);

  const saveRdays = async () => {
    try { const { data: d } = await api.put("/settings/factura-reminder-days", { value: parseInt(rdays || 0) }); setRdays(String(d.factura_reminder_days)); toast.success("Recordatorio configurado"); }
    catch { toast.error("No se pudo guardar"); }
  };
  const saveThr = async () => {
    try { const { data: d } = await api.put("/settings/cobranza-alert-threshold", { value: parseFloat(alertThr || 0) }); setAlertThr(String(d.cobranza_alert_threshold)); toast.success("Límite de alerta guardado"); load(); }
    catch { toast.error("No se pudo guardar"); }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Contratista", "Por pagar", "# por pagar", "Pagado"], ...data.por_contratista.map((r) => [r.contractor, r.por_pagar, r.count_por_pagar, r.pagado])];
    rows.push([], ["Total por pagar", data.total_por_pagar], ["Pagado (" + data.period_label + ")", data.pagado_periodo]);
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `cobranza_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!data) return;
    const win = window.open("", "_blank");
    const rowsHtml = data.por_contratista.map((r) => `<tr><td>${r.contractor}</td><td style="text-align:right">${money(r.por_pagar)}</td><td style="text-align:center">${r.count_por_pagar}</td><td style="text-align:right">${money(r.pagado)}</td></tr>`).join("");
    win.document.write(`<html><head><title>Cobranza</title><style>
      body{font-family:Arial,sans-serif;color:#0f172a;padding:32px}
      .brand{display:flex;align-items:center;gap:10px;border-bottom:3px solid #FF5A00;padding-bottom:12px;margin-bottom:20px}
      .brand .logo{width:38px;height:38px;background:#FF5A00;border-radius:8px;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800}
      h1{font-size:20px;margin:0}.sub{color:#64748b;font-size:12px}
      .cards{display:flex;gap:16px;margin:16px 0}
      .card{flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:14px}
      .card .lbl{font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:.05em}
      .card .val{font-size:24px;font-weight:800;margin-top:6px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}
      th,td{border-bottom:1px solid #e2e8f0;padding:8px;text-align:left}
      th{background:#f8fafc;text-transform:uppercase;font-size:11px;color:#64748b}
    </style></head><body>
      <div class="brand"><div class="logo">OC</div><div><h1>Obra en Control · Panel de Cobranza</h1><div class="sub">Plaza Tarantella · Generado ${new Date().toLocaleString("es-MX")}</div></div></div>
      <div class="cards">
        <div class="card"><div class="lbl">Aprobado por pagar</div><div class="val" style="color:#FF5A00">${money(data.total_por_pagar)}</div></div>
        <div class="card"><div class="lbl">Pagado (${data.period_label})</div><div class="val" style="color:#059669">${money(data.pagado_periodo)}</div></div>
      </div>
      <table><thead><tr><th>Contratista</th><th style="text-align:right">Por pagar</th><th style="text-align:center"># por pagar</th><th style="text-align:right">Pagado</th></tr></thead><tbody>${rowsHtml || '<tr><td colspan="4">Sin datos</td></tr>'}</tbody></table>
    </body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 400);
  };

  if (!data) return <div className="text-muted-foreground">Cargando…</div>;
  const maxProj = Math.max(1, ...data.projection.map((p) => p.amount));

  return (
    <div>
      <PageHeader overline="Finanzas" title="Panel de erogaciones"
        action={<div className="flex gap-2">
          <button onClick={exportCsv} data-testid="cobranza-export" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><DownloadSimple size={18} weight="bold" /> CSV</button>
          <button onClick={exportPdf} data-testid="cobranza-export-pdf" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><FilePdf size={18} weight="bold" /> PDF</button>
        </div>} />

      {data.over_alert && (
        <div className="mb-5 flex items-center gap-2 bg-red-50 border border-red-200 text-danger rounded-sm p-3 text-sm font-semibold" data-testid="cobranza-alert-banner">
          <Warning size={18} weight="fill" /> El total aprobado por pagar ({money(data.total_por_pagar)}) supera el límite configurado ({money(data.alert_threshold)}).
        </div>
      )}

      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><FunnelSimple size={16} weight="bold" /> Filtros</div>
          <div className="min-w-[200px]">
            <label className="overline block mb-1">Contratista</label>
            <select data-testid="cobranza-filter-contractor" className={inputClass} value={f.contractor_id} onChange={(e) => setF({ ...f, contractor_id: e.target.value })}>
              <option value="">Todos</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="overline block mb-1">Pagado desde</label><input type="date" data-testid="cobranza-filter-from" className={inputClass} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
          <div><label className="overline block mb-1">Pagado hasta</label><input type="date" data-testid="cobranza-filter-to" className={inputClass} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
          {(f.contractor_id || f.date_from || f.date_to) && <button onClick={() => setF({ contractor_id: "", date_from: "", date_to: "" })} className="h-11 px-3 text-sm text-muted-foreground hover:text-foreground">Limpiar</button>}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-6" data-testid="cobranza-por-pagar">
          <div className="flex items-center gap-2 overline"><Clock size={16} weight="bold" /> Aprobado por pagar</div>
          <div className="font-heading font-extrabold text-3xl text-primary mt-2">{money(data.total_por_pagar)}</div>
        </Card>
        <Card className="p-6" data-testid="cobranza-pagado">
          <div className="flex items-center gap-2 overline"><CheckCircle size={16} weight="bold" /> Pagado ({data.period_label})</div>
          <div className="font-heading font-extrabold text-3xl text-emerald-600 mt-2">{money(data.pagado_periodo)}</div>
        </Card>
      </div>

      <Card className="p-6 mb-6" data-testid="cobranza-projection">
        <div className="flex items-center justify-between overline mb-3">
          <span className="flex items-center gap-2"><TrendUp size={16} weight="bold" /> Proyección de pago (estimado a 15 días de aprobación)</span>
          <span className="flex items-center gap-3 normal-case tracking-normal text-xs text-muted-foreground"><span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Docs listos</span><span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Faltan docs</span></span>
        </div>
        {data.projection.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay estimaciones por pagar para proyectar.</p>
        ) : (
          <div className="space-y-2">
            {data.projection.map((p) => (
              <div key={p.week_start} className="flex items-center gap-3 text-sm">
                <span className="w-24 text-muted-foreground">Sem. {p.week_start.slice(8, 10)}/{p.week_start.slice(5, 7)}</span>
                <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden flex">
                  <div className="h-full bg-emerald-500" style={{ width: `${(p.ready / maxProj) * 100}%` }} title={`Listos: ${money(p.ready)}`} />
                  <div className="h-full bg-amber-400" style={{ width: `${(p.pending / maxProj) * 100}%` }} title={`Faltan docs: ${money(p.pending)}`} />
                </div>
                <span className="w-32 text-right font-semibold">{money(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 mb-6" data-testid="cobranza-monthly">
        <div className="flex items-center gap-2 overline mb-4"><ChartBar size={16} weight="bold" /> Pagado por mes (tendencia)</div>
        {(!data.monthly_paid || data.monthly_paid.length === 0) ? (
          <p className="text-sm text-muted-foreground">Aún no hay pagos registrados.</p>
        ) : (() => {
          const mx = Math.max(1, ...data.monthly_paid.map((m) => m.amount));
          return (
            <div className="flex items-end gap-3 h-48 overflow-x-auto pt-4">
              {data.monthly_paid.map((m) => (
                <div key={m.month} className="flex flex-col items-center justify-end flex-1 min-w-[54px]" title={money(m.amount)}>
                  <span className="text-[10px] font-semibold text-foreground mb-1">{money(m.amount)}</span>
                  <div className="w-full bg-emerald-500 rounded-t-sm" style={{ height: `${(m.amount / mx) * 100}%`, minHeight: 4 }} />
                  <span className="text-[10px] text-muted-foreground mt-1">{m.month.slice(5, 7)}/{m.month.slice(2, 4)}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>

      <Card className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">{["Contratista", "Por pagar", "# por pagar", "Pagado"].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}</tr></thead>
          <tbody>
            {data.por_contratista.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Sin movimientos de cobranza.</td></tr>
            ) : data.por_contratista.map((r) => (
              <tr key={r.contractor} data-testid={`cobranza-row-${r.contractor}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                <td className="px-4 py-3 font-medium">{r.contractor}</td>
                <td className="px-4 py-3 font-semibold text-primary">{money(r.por_pagar)}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.count_por_pagar}</td>
                <td className="px-4 py-3">{money(r.pagado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Historial de pagos */}
      <Card className="p-6 mb-6" data-testid="payments-history">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 overline"><ClockCounterClockwise size={16} weight="bold" /> Historial de pagos {history && <span className="normal-case tracking-normal text-muted-foreground font-normal">· {history.count} pagos · {money(history.total)}</span>}</div>
          <div className="flex items-end gap-2">
            <select data-testid="ph-contractor" className={inputClass} value={ph.contractor_id} onChange={(e) => setPh({ ...ph, contractor_id: e.target.value })}>
              <option value="">Todos los contratistas</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input type="month" data-testid="ph-month" className={inputClass} value={ph.month} onChange={(e) => setPh({ ...ph, month: e.target.value })} />
            {(ph.contractor_id || ph.month) && <button onClick={() => setPh({ contractor_id: "", month: "" })} className="h-11 px-3 text-sm text-muted-foreground hover:text-foreground">Limpiar</button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Fecha", "#", "Contratista", "Contrato", "Neto", "Docs"].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
            <tbody>
              {!history || history.payments.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sin pagos registrados.</td></tr>
              ) : history.payments.map((p) => (
                <tr key={p.id} data-testid={`payment-${p.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-3 py-2">{p.paid_at}</td>
                  <td className="px-3 py-2 font-bold text-primary">#{p.number}</td>
                  <td className="px-3 py-2">{p.contractor}</td>
                  <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">{p.contract}</td>
                  <td className="px-3 py-2 font-semibold">{money(p.net)}</td>
                  <td className="px-3 py-2 text-xs">{p.has_factura ? "✓F" : "—"} {p.has_comprobante ? "✓C" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {canConfig && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-5" data-testid="cobranza-reminder-config">
            <div className="flex items-center gap-2 overline mb-2"><Gear size={16} weight="bold" /> Recordatorio de factura</div>
            <p className="text-sm text-muted-foreground mb-3">Días de espera antes de recordarle al contratista subir factura y comprobante.</p>
            <div className="flex items-center gap-2">
              <input type="number" min="0" data-testid="reminder-days-input" className={inputClass + " w-24"} value={rdays} onChange={(e) => setRdays(e.target.value)} />
              <span className="text-sm text-muted-foreground">día(s)</span>
              <button onClick={saveRdays} data-testid="reminder-days-save" className="h-11 px-4 bg-primary text-white rounded-sm font-semibold text-sm hover:bg-orange-600 transition-colors">Guardar</button>
            </div>
          </Card>
          <Card className="p-5" data-testid="cobranza-threshold-config">
            <div className="flex items-center gap-2 overline mb-2"><Warning size={16} weight="bold" /> Alerta de cobranza alta</div>
            <p className="text-sm text-muted-foreground mb-3">Avisa cuando el total aprobado por pagar supere este monto (0 = desactivado).</p>
            <div className="flex items-center gap-2">
              <input type="number" min="0" step="1000" data-testid="alert-threshold-input" className={inputClass + " w-40"} value={alertThr} onChange={(e) => setAlertThr(e.target.value)} />
              <button onClick={saveThr} data-testid="alert-threshold-save" className="h-11 px-4 bg-primary text-white rounded-sm font-semibold text-sm hover:bg-orange-600 transition-colors">Guardar</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
